import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  ScrollView,
  Image,
} from 'react-native';
import { InsetModal as Modal } from './InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from './DirectionalIcon';
import { colors, spacing, typography } from '../theme';
import { useMoreOptionsData } from '../hooks/useMoreOptionsData';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface MoreOptionsModalProps {
  visible: boolean;
  onClose: () => void;
}

export const MoreOptionsModal: React.FC<MoreOptionsModalProps> = ({
  visible,
  onClose,
}) => {
  const styles = useThemedStyles(getStyles);
  const {
    user,
    currentBusiness,
    userBusinesses,
    businessRole,
    isLoadingBusinesses,
    imageError,
    setImageError,
    moreOptions,
    switchBusiness,
    createBusiness,
  } = useMoreOptionsData(onClose, visible);

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      onRequestClose={onClose}
      presentationStyle={Platform.OS === 'ios' ? 'pageSheet' : undefined}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {/* Header */}
          <View style={styles.modalHeader}>
            <View style={styles.userInfo}>
              <View style={styles.userAvatar}>
                {(() => {
                  // Get profile picture URL from avatar or profilePicture field
                  const profilePictureUrl = user?.avatar || user?.profilePicture;
                  const hasValidProfilePicture = !imageError && 
                                                 profilePictureUrl && 
                                                 typeof profilePictureUrl === 'string' &&
                                                 profilePictureUrl.trim() !== '' && 
                                                 profilePictureUrl !== 'null' && 
                                                 profilePictureUrl !== 'undefined' &&
                                                 (profilePictureUrl.startsWith('http://') || 
                                                  profilePictureUrl.startsWith('https://') || 
                                                  profilePictureUrl.startsWith('file://') || 
                                                  profilePictureUrl.startsWith('data:'));
                  
                  // Get user initials
                  const firstName = user?.firstName || user?.name?.split(' ')[0] || '';
                  const lastName = user?.lastName || user?.name?.split(' ').slice(1).join(' ') || '';
                  const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
                  const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
                  const initials = (firstInitial + lastInitial) || 'U';
                  
                  // Show profile picture if available and no error, otherwise show initials
                  if (hasValidProfilePicture) {
                    return (
                      <>
                        {Platform.OS === 'web' ? (
                          <img
                            src={profilePictureUrl}
                            alt="Profile"
                            style={{
                              width: 50,
                              height: 50,
                              borderRadius: 25,
                              objectFit: 'cover' as const,
                              display: 'block',
                            }}
                            onError={() => {
                              setImageError(true);
                            }}
                          />
                        ) : (
                          <Image
                            source={{ uri: profilePictureUrl }}
                            style={styles.userAvatarImage}
                            onError={() => {
                              setImageError(true);
                            }}
                          />
                        )}
                      </>
                    );
                  }
                  
                  // Show initials when no profile picture or image error
                  return <Text style={styles.userInitials}>{initials}</Text>;
                })()}
              </View>
              <View style={styles.userDetails}>
                <Text style={styles.userName}>
                  {user?.firstName && user?.lastName 
                    ? `${user.firstName} ${user.lastName}` 
                    : `${user?.firstName || 'User'} ${user?.lastName || 'Name'}`
                  }
                </Text>
                <Text style={styles.userEmail}>{user?.email || 'user@example.com'}</Text>
              </View>
            </View>
            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <Ionicons name="close" size={24} color={colors.textLight} />
            </TouchableOpacity>
          </View>

          {/* Scrollable Content */}
          <ScrollView 
            style={styles.scrollableContent} 
            contentContainerStyle={styles.scrollableContentContainer}
            showsVerticalScrollIndicator={false}
          >
            {/* Business Switching Section */}
            {isLoadingBusinesses ? (
              <View style={styles.businessSection}>
                <Text style={styles.businessSectionTitle}>Loading Businesses...</Text>
                <View style={styles.loadingBusiness}>
                  <Ionicons name="refresh" size={20} color={colors.textLight} />
                  <Text style={styles.loadingText}>Loading your businesses...</Text>
                </View>
              </View>
            ) : userBusinesses.length > 0 ? (
              <View style={styles.businessSection}>
                <Text style={styles.businessSectionTitle}>Current Business</Text>
                <View style={styles.currentBusiness}>
                  <View style={styles.businessIcon}>
                    <Ionicons name="business" size={24} color={colors.primary} />
                  </View>
                  <View style={styles.businessInfo}>
                    <Text style={styles.businessName}>
                      {currentBusiness?.name || userBusinesses[0]?.name || 'Business Name'}
                    </Text>
                    <Text style={styles.businessRole}>{businessRole || 'Owner'}</Text>
                  </View>
                  <TouchableOpacity
                    style={styles.switchButton}
                    onPress={switchBusiness}
                  >
                    <Ionicons name="swap-horizontal" size={20} color={colors.primary} />
                  </TouchableOpacity>
                </View>
                
                {userBusinesses.length > 1 && (
                  <View style={styles.otherBusinesses}>
                    <Text style={styles.otherBusinessesTitle}>Other Businesses</Text>
                    <ScrollView style={styles.businessesList} showsVerticalScrollIndicator={false}>
                      {userBusinesses.slice(1).map((business, index) => (
                        <TouchableOpacity
                          key={business._id || index}
                          style={styles.otherBusinessItem}
                          onPress={switchBusiness}
                        >
                          <View style={styles.otherBusinessIcon}>
                            <Ionicons name="business-outline" size={20} color={colors.textLight} />
                          </View>
                          <Text style={styles.otherBusinessName}>{business.name}</Text>
                          <DirectionalIcon name="chevron-forward" size={16} color={colors.textLight} />
                        </TouchableOpacity>
                      ))}
                    </ScrollView>
                  </View>
                )}
              </View>
            ) : (
              <View style={styles.businessSection}>
                <Text style={styles.businessSectionTitle}>No Businesses</Text>
                <View style={styles.noBusiness}>
                  <Ionicons name="business-outline" size={24} color={colors.textLight} />
                  <Text style={styles.noBusinessText}>You haven't joined any businesses yet</Text>
                  <TouchableOpacity
                    style={styles.createBusinessButton}
                    onPress={createBusiness}
                  >
                    <Text style={styles.createBusinessButtonText}>Create Business</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}

            {/* Options List */}
            <View style={styles.optionsList}>
              {moreOptions
                .map((option) => (
                  <TouchableOpacity
                    key={option.id}
                    style={styles.optionItem}
                    onPress={option.action}
                    activeOpacity={0.7}
                  >
                    <View style={styles.optionIcon}>
                      <Ionicons name={option.icon as any} size={24} color={colors.primary} />
                    </View>
                    <Text style={styles.optionTitle}>{option.title}</Text>
                    <DirectionalIcon name="chevron-forward" size={20} color={colors.textLight} />
                  </TouchableOpacity>
                ))}
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContainer: {
    backgroundColor: colors.background.surfaceRaised,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
    flex: 1,
  },
  scrollableContent: {
    flex: 1,
  },
  scrollableContentContainer: {
    paddingBottom: Platform.OS === 'ios' ? 34 : spacing.lg,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,  // Use light border color
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  userAvatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
    overflow: 'hidden',
  },
  userAvatarImage: {
    width: 50,
    height: 50,
    borderRadius: 25,
  },
  userInitials: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.white,
    fontFamily: typography.fontFamily.semibold,
  },
  userDetails: {
    flex: 1,
  },
  userName: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    marginBottom: spacing.xs,
    fontFamily: typography.fontFamily.semibold,
  },
  userEmail: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  closeButton: {
    padding: spacing.sm,
  },
  businessSection: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  businessSectionTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.textLight,
    marginBottom: spacing.sm,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontFamily: typography.fontFamily.semibold,
  },
  currentBusiness: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.lightGray,
    padding: spacing.md,
    borderRadius: 12,
    marginBottom: spacing.sm,
  },
  businessIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.background.secondary,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  businessInfo: {
    flex: 1,
  },
  businessName: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    marginBottom: spacing.xs,
    fontFamily: typography.fontFamily.semibold,
  },
  businessRole: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  switchButton: {
    padding: spacing.sm,
    backgroundColor: colors.background.surface,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  otherBusinesses: {
    marginTop: spacing.sm,
  },
  otherBusinessesTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.textLight,
    marginBottom: spacing.sm,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontFamily: typography.fontFamily.semibold,
  },
  businessesList: {
    maxHeight: 120,
  },
  otherBusinessItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    backgroundColor: colors.background.secondary,
    borderRadius: 8,
    marginBottom: spacing.xs,
    borderWidth: 1,
    borderColor: colors.border,
  },
  otherBusinessIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.lightGray,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  otherBusinessName: {
    fontSize: typography.fontSize.sm,
    fontWeight: '500',
    color: colors.textDark,
    flex: 1,
    fontFamily: typography.fontFamily.medium,
  },
  loadingBusiness: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.md,
    backgroundColor: colors.lightGray,
    borderRadius: 12,
  },
  loadingText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    marginLeft: spacing.sm,
    fontFamily: typography.fontFamily.regular,
  },
  noBusiness: {
    alignItems: 'center',
    padding: spacing.lg,
    backgroundColor: colors.lightGray,
    borderRadius: 12,
  },
  noBusinessText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    textAlign: 'center',
    marginTop: spacing.sm,
    marginBottom: spacing.md,
    fontFamily: typography.fontFamily.regular,
  },
  createBusinessButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: 20,
  },
  createBusinessButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    fontFamily: typography.fontFamily.semibold,
  },
  optionsList: {
    paddingTop: spacing.md,
  },
  optionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,  // Use light border color
  },
  optionIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.lightGray,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  optionTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: '500',
    color: colors.textDark,
    flex: 1,
    fontFamily: typography.fontFamily.medium,
  },
});
